import {
  IActionMapping,
  TreeComponent,
  TreeNode
} from '@ali-hm/angular-tree-component';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { BuilderLeftEnum } from '#common/enums/builder-left.enum';
import { FileExtensionEnum } from '#common/enums/file-extension.enum';
import { decodeFilePath } from '#common/functions/decode-file-path';
import { encodeFilePath } from '#common/functions/encode-file-path';
import { isDefined } from '#common/functions/is-defined';
import { isDefinedAndNotEmpty } from '#common/functions/is-defined-and-not-empty';
import { isUndefined } from '#common/functions/is-undefined';
import { makeId } from '#common/functions/make-id';
import type { DiskFileLine } from '#common/zod/disk/disk-file-line';
import type { BmlErrorExtra } from '#common/zod/front/bml-error-extra';
import { getFileExtension } from '#front/app/functions/get-file-extension';
import { FileQuery } from '#front/app/queries/file.query';
import { NavQuery } from '#front/app/queries/nav.query';
import { StructQuery, StructState } from '#front/app/queries/struct.query';
import { NavigateService } from '#front/app/services/navigate.service';

export class BmlErrorsNode {
  title: string;
  message: string;
  lines: DiskFileLine[];
  id: string;
  name: string;
  isError: boolean;
  children: BmlErrorsNode[];
}

@Component({
  standalone: false,
  selector: 'm-blockml-errors',
  templateUrl: './blockml-errors.component.html',
  styleUrls: ['blockml-errors.component.scss']
})
export class BlockmlErrorsComponent implements OnDestroy {
  @Input() isEditor: boolean;
  @Input() needSave: boolean;
  @Input() isProduction: boolean;

  @Output() validateClick = new EventEmitter<void>();

  errors: BmlErrorExtra[];

  nodes: BmlErrorsNode[];

  struct: StructState;
  struct$ = this.structQuery.select().pipe(
    tap(x => {
      this.struct = x;

      this.errors = x.errors
        .map(error => {
          let errorExt =
            error.lines.length > 0
              ? getFileExtension(error.lines[0].fileId)
              : undefined;

          return Object.assign({}, error, <BmlErrorExtra>{
            errorExt: errorExt,
            sortOrder: isDefined(errorExt) ? this.errorSortOrder(errorExt) : 1,
            title: error.title
          });
        })
        .sort((a, b) => {
          if (a.sortOrder < b.sortOrder) {
            return -1;
          } else if (a.sortOrder > b.sortOrder) {
            return 1;
          } else {
            return 0;
          }
        });

      let nodes: BmlErrorsNode[] = [];

      this.errors.forEach(error => {
        let errorId = makeId();

        if (error.lines.length > 0) {
          error.lines.forEach(line => {
            let newErrorNode: BmlErrorsNode = {
              title: error.title,
              message: error.message,
              id: errorId,
              name: null,
              isError: true,
              lines: [line],
              children: []
            };

            let node = nodes.find(y => y.id === line.fileId);

            if (isUndefined(node)) {
              let newNode: BmlErrorsNode = {
                id: line.fileId,
                name: line.fileName,
                title: undefined,
                message: undefined,
                lines: undefined,
                children: [newErrorNode],
                isError: false
              };

              nodes.push(newNode);
            } else {
              let eNode = node.children.find(k => k.id === errorId);

              if (isDefined(eNode)) {
                eNode.lines.push(line);
              } else {
                node.children.push(newErrorNode);
              }
            }
          });
        } else {
          let newErrorNode: BmlErrorsNode = {
            title: error.title,
            message: error.message,
            id: errorId,
            name: null,
            isError: true,
            lines: [],
            children: []
          };

          let newNode: BmlErrorsNode = {
            id: undefined,
            name: 'No file',
            title: undefined,
            message: undefined,
            lines: undefined,
            children: [newErrorNode],
            isError: false
          };

          nodes.push(newNode);
        }
      });

      this.nodes = nodes;

      this.cd.detectChanges();
    })
  );

  actionMapping: IActionMapping = {
    mouse: {}
  };

  treeOptions = {
    actionMapping: this.actionMapping,
    displayField: 'name'
  };

  @ViewChild('itemsTree') itemsTree: TreeComponent;

  expandLevel$: Subscription;

  fileNodeId: string;

  file$ = this.fileQuery.select().pipe(
    tap(x => {
      if (isUndefined(x.fileId)) {
        this.fileNodeId = undefined;
        this.cd.detectChanges();
        return;
      }

      let projectId;

      this.navQuery
        .select()
        .pipe(
          tap(y => {
            projectId = y.projectId;
          }),
          take(1)
        )
        .subscribe();

      let filePath = decodeFilePath({ filePath: x.fileId });

      let fIdAr = filePath.split('/');

      this.fileNodeId = [projectId, ...fIdAr].join('/');
      this.cd.detectChanges();
    })
  );

  constructor(
    private fileQuery: FileQuery,
    private structQuery: StructQuery,
    private navigateService: NavigateService,
    private navQuery: NavQuery,
    private cd: ChangeDetectorRef
  ) {}

  treeOnInitialized() {
    if (this.nodes.length === 0) {
      return;
    }

    this.expandLevel$ = this.fileQuery
      .select()
      .pipe(
        tap(x => {
          let projectId: string;
          this.navQuery.projectId$
            .pipe(
              tap(y => {
                projectId = y;
              }),
              take(1)
            )
            .subscribe();

          if (isDefined(x.fileId)) {
            let filePath = decodeFilePath({ filePath: x.fileId });

            let fileIdAr = filePath.split('/');

            let fileId = [projectId, ...fileIdAr].join('/');

            let node = this.itemsTree.treeModel.getNodeById(fileId);

            if (isDefined(node)) {
              node.expand();
            }
          }

          this.cd.detectChanges();
        })
      )
      .subscribe();
  }

  treeOnUpdateData() {}

  nodeOnClick(node: TreeNode) {
    node.toggleExpanded();
  }

  errorSortOrder(errorExt: string): number {
    let ext = `.${errorExt}`;

    switch (ext) {
      case 'other':
        return 1;
      case FileExtensionEnum.Yml:
        return 2;
      case FileExtensionEnum.Md:
        return 3;
      case FileExtensionEnum.Space:
        return 4;
      case FileExtensionEnum.Store:
        return 5;
      case FileExtensionEnum.Report:
        return 6;
      case FileExtensionEnum.Dashboard:
        return 7;
      case FileExtensionEnum.Chart:
        return 8;
      default:
        return 0;
    }
  }

  goToFileLine(line: DiskFileLine) {
    if (isDefinedAndNotEmpty(line.fileId)) {
      let lineFileIdAr = line.fileId.split('/');
      lineFileIdAr.shift();

      let filePath = lineFileIdAr.join('/');

      let fileId = encodeFilePath({ filePath: filePath });

      this.navigateService.navigateToFileLine({
        builderLeft: BuilderLeftEnum.Tree,
        encodedFileId: fileId,
        lineNumber: line.lineNumber
      });
    }
  }

  ngOnDestroy() {
    this.expandLevel$?.unsubscribe();
  }
}
